import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Select,
  InputNumber,
  Row,
  Col,
  Tabs,
  Card,
  Descriptions,
  Alert,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { autoReplyAPI, botAPI, commandAPI } from '../services/api';
import GroupAutoReplyEditModal from '../components/GroupAutoReplyEditModal';

const { Title, Text } = Typography;

interface AutoReply {
  id: number;
  bot_id: number | null;
  group_id: number | null;
  match_type: string;
  keyword: string;
  reply_text: string;
  reply_type: string;
  media_url: string | null;
  parse_mode: string;
  delete_trigger: number;
  reply_probability: number;
  permission_level: number;
  inline_buttons: any;
  dynamic_variables: any;
  sub_pages: any;
  priority: number;
  status: number;
  description: string | null;
  create_time: string;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
}

interface Command {
  id: string;
  command: string;
  description: string;
  category: string;
  permission: string;
  statusRequired: string;
  example: string;
  response: string;
  notes: string;
  implementedIn: string;
}

const GroupAutoReplies: React.FC = () => {
  const [rules, setRules] = useState<AutoReply[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoReply | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<{
    botId?: number | null;
    groupId?: number | null;
    status?: number;
  }>({});

  const [commands, setCommands] = useState<Command[]>([]);
  const [commandsLoading, setCommandsLoading] = useState(false);
  const [commandsByCategory, setCommandsByCategory] = useState<any>({});
  const [categoryNames, setCategoryNames] = useState<any>({});
  const [permissionNames, setPermissionNames] = useState<any>({});
  const [statusNames, setStatusNames] = useState<any>({});

  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch {
      message.error('获取机器人列表失败');
    }
  };

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await autoReplyAPI.getAll(filters);
      setRules(response.data);
    } catch {
      message.error('获取自动回复规则失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommands = async () => {
    setCommandsLoading(true);
    try {
      const response = await commandAPI.getAll();
      setCommands(response.data.data.commands);
      setCategoryNames(response.data.data.metadata.categories);
      setPermissionNames(response.data.data.metadata.permissions);
      setStatusNames(response.data.data.metadata.statuses);
      const grouped: any = {};
      response.data.data.commands.forEach((cmd: Command) => {
        if (!grouped[cmd.category]) grouped[cmd.category] = [];
        grouped[cmd.category].push(cmd);
      });
      setCommandsByCategory(grouped);
    } catch {
      message.error('获取命令列表失败');
    } finally {
      setCommandsLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
    fetchCommands();
  }, []);

  useEffect(() => {
    fetchRules();
  }, [filters]);

  const openModal = (rule?: AutoReply) => {
    setEditingRule(rule || null);
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        botId: values.botId || null,
        groupId: values.groupId || null,
        matchType: values.matchType,
        keyword: values.keyword,
        replyText: values.replyText,
        replyType: values.replyType,
        mediaUrl: values.replyType !== 'text' ? values.mediaUrl : null,
        parseMode: values.parseMode,
        deleteTrigger: values.deleteTrigger ? 1 : 0,
        replyProbability: values.replyProbability,
        permissionLevel: values.permissionLevel || 0,
        inlineButtons: values.inlineButtons?.length > 0 ? values.inlineButtons : null,
        dynamicVariables: values.dynamicVariables || null,
        subPages: values.subPages?.length > 0 ? values.subPages : null,
        priority: values.priority,
        status: values.status ? 1 : 0,
        description: values.description,
      };

      if (editingRule) {
        await autoReplyAPI.update(editingRule.id, data);
        message.success('规则已更新');
      } else {
        await autoReplyAPI.create(data);
        message.success('规则已创建');
      }

      setModalVisible(false);
      fetchRules();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await autoReplyAPI.delete(id);
      message.success('规则已删除');
      fetchRules();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleBatchUpdateStatus = async (status: number) => {
    if (selectedRowKeys.length === 0) { message.warning('请先选择规则'); return; }
    try {
      await autoReplyAPI.batchUpdateStatus(selectedRowKeys as number[], status);
      message.success(`已批量${status === 1 ? '启用' : '禁用'}`);
      setSelectedRowKeys([]);
      fetchRules();
    } catch (error: any) {
      message.error(error.response?.data?.error || '批量操作失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) { message.warning('请先选择规则'); return; }
    try {
      await autoReplyAPI.batchDelete(selectedRowKeys as number[]);
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchRules();
    } catch (error: any) {
      message.error(error.response?.data?.error || '批量删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '机器人', dataIndex: 'bot_id', width: 120,
      render: (botId: number | null) => {
        if (botId === null) return <Tag color="purple">全局</Tag>;
        const bot = bots.find(b => b.id === botId);
        return bot ? bot.bot_name : `ID:${botId}`;
      },
    },
    {
      title: '群组ID', dataIndex: 'group_id', width: 120,
      render: (groupId: number | null) => groupId === null ? <Tag color="blue">所有群组</Tag> : groupId,
    },
    {
      title: '匹配类型', dataIndex: 'match_type', width: 100,
      render: (type: string) => {
        const map: Record<string, [string, string]> = {
          exact: ['green', '精确'], contains: ['blue', '包含'], regex: ['orange', '正则'],
        };
        const [color, label] = map[type] || ['default', type];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    { title: '关键词', dataIndex: 'keyword', width: 150, ellipsis: true },
    {
      title: '回复内容', dataIndex: 'reply_text', width: 200, ellipsis: true,
      render: (text: string) => <Text ellipsis title={text}>{text}</Text>,
    },
    {
      title: '类型', dataIndex: 'reply_type', width: 80,
      render: (type: string) => {
        const map: Record<string, [string, string]> = {
          text: ['default', '文本'], photo: ['cyan', '图片'], video: ['purple', '视频'],
        };
        const [color, label] = map[type] || ['default', type];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '子页面', dataIndex: 'sub_pages', width: 80,
      render: (sub: any) => {
        const pages = Array.isArray(sub) ? sub : (sub ? (() => { try { return JSON.parse(sub); } catch { return []; } })() : []);
        return pages.length > 0 ? <Tag color="geekblue">{pages.length} 页</Tag> : null;
      },
    },
    {
      title: '概率', dataIndex: 'reply_probability', width: 80,
      render: (prob: number) => `${prob}%`,
    },
    {
      title: '权限', dataIndex: 'permission_level', width: 80,
      render: (level: number) => level === 1
        ? <Tag color="red">操作人</Tag>
        : <Tag color="green">所有人</Tag>,
    },
    { title: '优先级', dataIndex: 'priority', width: 80 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (status: number) => status === 1
        ? <Tag icon={<CheckCircleOutlined />} color="success">启用</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="default">禁用</Tag>,
    },
    {
      title: '操作', width: 150, fixed: 'right' as const,
      render: (_: any, record: AutoReply) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>编辑</Button>
          <Popconfirm title="确定删除此规则吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>群组自动回复管理</Title>

      <Tabs
        defaultActiveKey="keyword"
        items={[
          {
            key: 'keyword',
            label: <span><BulbOutlined /> 关键词触发回复</span>,
            children: (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                      <Select placeholder="按机器人筛选" allowClear style={{ width: '100%' }}
                        onChange={value => setFilters({ ...filters, botId: value })}>
                        <Select.Option value={null}>全局规则</Select.Option>
                        {bots.map(bot => (
                          <Select.Option key={bot.id} value={bot.id}>{bot.bot_name}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={6}>
                      <InputNumber placeholder="按群组ID筛选" style={{ width: '100%' }}
                        onChange={value => setFilters({ ...filters, groupId: value as number | null })} />
                    </Col>
                    <Col span={6}>
                      <Select placeholder="按状态筛选" allowClear style={{ width: '100%' }}
                        onChange={value => setFilters({ ...filters, status: value })}>
                        <Select.Option value={1}>启用</Select.Option>
                        <Select.Option value={0}>禁用</Select.Option>
                      </Select>
                    </Col>
                  </Row>
                  <Space>
                    <Button icon={<ReloadOutlined />} onClick={fetchRules}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>新建规则</Button>
                    <Button onClick={() => handleBatchUpdateStatus(1)} disabled={selectedRowKeys.length === 0}>批量启用</Button>
                    <Button onClick={() => handleBatchUpdateStatus(0)} disabled={selectedRowKeys.length === 0}>批量禁用</Button>
                    <Popconfirm
                      title={`确定删除选中的 ${selectedRowKeys.length} 条规则吗？`}
                      onConfirm={handleBatchDelete}
                      okText="确定" cancelText="取消"
                      disabled={selectedRowKeys.length === 0}
                    >
                      <Button danger icon={<DeleteOutlined />} disabled={selectedRowKeys.length === 0}>批量删除</Button>
                    </Popconfirm>
                  </Space>
                </div>

                <Table
                  columns={columns}
                  dataSource={rules}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1400 }}
                  rowSelection={{ selectedRowKeys, onChange: keys => setSelectedRowKeys(keys) }}
                />

                <GroupAutoReplyEditModal
                  visible={modalVisible}
                  editingRule={editingRule}
                  bots={bots}
                  onSubmit={handleSubmit}
                  onCancel={() => setModalVisible(false)}
                />
              </div>
            ),
          },
          {
            key: 'command',
            label: <span><CodeOutlined /> 命令式回复</span>,
            children: (
              <div>
                <Alert
                  message="命令式回复说明"
                  description="这些命令是硬编码在代码中的，不能通过后台管理界面修改。它们用于处理复杂的业务逻辑（如记账、权限管理等）。"
                  type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {Object.keys(commandsByCategory).map(category => (
                    <Card key={category} title={categoryNames[category] || category} size="small">
                      <Table
                        dataSource={commandsByCategory[category]}
                        rowKey="id"
                        loading={commandsLoading}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: '命令', dataIndex: 'command', width: 200,
                            render: (text: string) => <Tag color="blue"><code>{text}</code></Tag>,
                          },
                          { title: '说明', dataIndex: 'description', width: 200 },
                          {
                            title: '权限', dataIndex: 'permission', width: 100,
                            render: (permission: string) => (
                              <Tag color={permission === 'all' ? 'green' : permission === 'operator' ? 'orange' : 'red'}>
                                {permissionNames[permission] || permission}
                              </Tag>
                            ),
                          },
                          {
                            title: '状态要求', dataIndex: 'statusRequired', width: 120,
                            render: (status: string) => (
                              <Tag color={status === 'initialized' ? 'orange' : 'default'}>
                                {statusNames[status] || status}
                              </Tag>
                            ),
                          },
                          {
                            title: '示例', dataIndex: 'example', width: 180,
                            render: (text: string) => <Text code style={{ fontSize: 12 }}>{text}</Text>,
                          },
                          {
                            title: '详情', width: 80,
                            render: (_: any, record: Command) => (
                              <Button size="small" type="link" onClick={() => {
                                Modal.info({
                                  title: `命令详情：${record.command}`,
                                  width: 600,
                                  content: (
                                    <Descriptions column={1} bordered size="small">
                                      <Descriptions.Item label="命令"><Tag color="blue"><code>{record.command}</code></Tag></Descriptions.Item>
                                      <Descriptions.Item label="说明">{record.description}</Descriptions.Item>
                                      <Descriptions.Item label="权限">{permissionNames[record.permission] || record.permission}</Descriptions.Item>
                                      <Descriptions.Item label="示例"><Text code>{record.example}</Text></Descriptions.Item>
                                      <Descriptions.Item label="预期响应">{record.response}</Descriptions.Item>
                                      <Descriptions.Item label="注意事项">{record.notes}</Descriptions.Item>
                                    </Descriptions>
                                  ),
                                });
                              }}>查看</Button>
                            ),
                          },
                        ]}
                      />
                    </Card>
                  ))}
                </Space>
                <Alert
                  message="命令优先级说明"
                  description="命令式回复的优先级高于关键词触发回复。"
                  type="warning" showIcon style={{ marginTop: 16 }}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default GroupAutoReplies;
